/**
 * Week-check in de weektabel van /draaidagen (Joris, 6 okt 2026):
 *  - één rustige badge per week ("3 over max" / "2 op max" / "1 volle agenda");
 *  - een naam in de popover markeert de draaidagen van die persoon in de tabel.
 *
 * Alles hier is puur (geen React), zodat de matching los te testen is.
 */
import { crewUitTitel } from '../../utils/draaidagCrew';

/** Badge-tekst + toon voor een week-check; null = niets bijzonders (rustig houden). */
export function weekBadge(wc) {
  if (!wc) return null;
  const over = (wc.over || []).length;
  const opMax = (wc.op_max || []).length;
  const druk = (wc.druk_met_agenda || []).length;
  if (wc.status === 'over' && over) return { toon: 'rood', tekst: `${over} over max` };
  if (opMax || druk) {
    const delen = [];
    if (opMax) delen.push(`${opMax} op max`);
    if (druk) delen.push(`${druk} volle agenda`);
    return { toon: 'oranje', tekst: delen.join(' · ') };
  }
  return null;
}

/** Korte samenvatting voor de hover-tooltip van de badge. */
export function weekBadgeUitleg(wc) {
  if (!wc) return '';
  const namen = (lijst) => (lijst || []).map((i) => i.naam).join(', ');
  const regels = [];
  if ((wc.over || []).length) regels.push(`Over het maximum: ${namen(wc.over)}`);
  if ((wc.op_max || []).length) regels.push(`Op het maximum: ${namen(wc.op_max)}`);
  if ((wc.druk_met_agenda || []).length) regels.push(`Volle agenda: ${namen(wc.druk_met_agenda)}`);
  if (!regels.length) regels.push('Deze week past binnen de regels');
  regels.push('Klik voor de uitleg per persoon');
  return regels.join('\n');
}

const norm = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();
const voornaam = (s) => norm(s).split(' ')[0] || '';

/**
 * Hoort crew-naam `crew` bij persoon `naam`? Zoals de engine telt: de volledige
 * naam, of — alleen als `crew` een losse voornaam is ("Raijmond" in de titel) —
 * dezelfde voornaam (≥3 letters), behalve als die voornaam meerduidig is.
 * Twee volledige namen met dezelfde voornaam (Robin Jansen ≠ Robin Smit) matchen niet.
 */
export function zelfdePersoon(naam, crew, ambigueVoornamen = null) {
  const a = norm(naam);
  const b = norm(String(crew || '').replace(/\(.*?\)/g, '').replace(/\?/g, ''));
  if (!a || !b) return false;
  if (a === b) return true;
  if (b.includes(' ')) return false;
  const va = voornaam(a);
  if (ambigueVoornamen && ambigueVoornamen.has(va)) return false;
  return va.length >= 3 && va === b;
}

/** Voornamen die in de week-check van een week meer dan eens voorkomen. */
export function ambigueVoornamenUit(wc, extraNamen = []) {
  const tel = new Map();
  const gezien = new Set();
  const namen = [
    ...['over', 'op_max', 'druk_met_agenda', 'ruimte'].flatMap((k) => (wc?.[k] || []).map((i) => i.naam)),
    ...(extraNamen || []),
  ];
  namen.forEach((naam) => {
    const n = norm(naam);
    if (!n || gezien.has(n)) return;
    gezien.add(n);
    const v = voornaam(n);
    tel.set(v, (tel.get(v) || 0) + 1);
  });
  return new Set([...tel.entries()].filter(([, n]) => n > 1).map(([v]) => v));
}

/**
 * Bezette uren op een dag uit het week-detail ("09:00–13:00"), met overlap maar
 * één keer geteld — zoals de engine (_bezette_uren).
 */
export const urenVan = (afspraken) => {
  const blokken = (afspraken || []).map((a) => {
    const m = String(a?.tijd || '').match(/(\d{1,2})[:.](\d{2})\D+(\d{1,2})[:.](\d{2})/);
    return m ? [Number(m[1]) * 60 + Number(m[2]), Number(m[3]) * 60 + Number(m[4])] : null;
  }).filter((b) => b && b[1] > b[0]).sort((x, y) => x[0] - y[0]);
  let totaal = 0;
  let van = null;
  let tot = null;
  blokken.forEach(([s, e]) => {
    if (van === null) { van = s; tot = e; } else if (s <= tot) { tot = Math.max(tot, e); } else { totaal += tot - van; van = s; tot = e; }
  });
  if (van !== null) totaal += tot - van;
  return totaal / 60;
};
/** Zelfde drempel als de engine: ≥4 uur afspraken op een dag = volle agenda. */
export const VOLLE_AGENDA_UREN = 4;

/** Ligt deze draaidag (deels) in de week die op `weekKey` (maandag) begint? */
export function rijInWeek(r, weekKey) {
  if (!weekKey) return false;
  const eind = dagNa(weekKey, 6);
  return dagenVanRij(r).some((d) => d >= weekKey && d <= eind);
}

const dagNa = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Datums (YYYY-MM-DD) die een draaidag beslaat. */
export function dagenVanRij(r) {
  const start = String(r.startDatum || '').slice(0, 10);
  if (!start) return [];
  let eind = r.eindDatum && r.eindDatum > start ? String(r.eindDatum).slice(0, 10) : null;
  if (!eind && Number(r.dagen) > 1) eind = dagNa(start, Number(r.dagen) - 1);
  const uit = [start];
  for (let i = 1; eind && uit[uit.length - 1] < eind && i < 31; i += 1) uit.push(dagNa(start, i));
  return uit;
}

/** Een titel met OPTIE is nog niet hard bevestigd — zelfde regel als de engine (_is_optie). */
export const isOptie = (r) => /optie/i.test(String(r.origineleTitel || r.titel || ''));

/**
 * Welke rijen (en dagen) horen bij deze persoon in deze week?
 *
 * @param rows    draaidag-rijen zoals de pagina ze toont
 * @param doel    { naam, weekKey (maandag), soort: 'over'|'op_max'|'druk'|'ruimte', ambigueVoornamen?: Set }
 * @param detail  optioneel: week-detail van de persoon ({ dagen: [{datum, draaidagen, afspraken}] }).
 *                Zonder detail worden er bij 'druk' nog geen dagen gemarkeerd (we weten niet welke).
 * @returns {{ rijen: Map<string, {optie: boolean, dagen: string[], crewNaam: string|null, titelNaam: string|null}>, soort: string }}
 */
export function markeringVoorPersoon(rows, doel, detail = null) {
  const rijen = new Map();
  if (!doel || !doel.naam || !doel.weekKey) return { rijen, soort: doel?.soort || null };
  const weekEind = dagNa(doel.weekKey, 6);
  // Bij "volle agenda": de dagen met ≥4 uur afspraken (engine-drempel) waarop
  // deze persoon ook een draaidag heeft. Pas bekend zodra het detail er is.
  const drukkeDagen = new Set((detail?.dagen || [])
    .filter((d) => urenVan(d.afspraken) >= VOLLE_AGENDA_UREN)
    .map((d) => d.datum));

  (rows || []).forEach((r) => {
    const dagen = dagenVanRij(r).filter((d) => d >= doel.weekKey && d <= weekEind);
    if (!dagen.length) return;
    const amb = doel.ambigueVoornamen || null;
    const crewNaam = (r.crew || []).find((c) => zelfdePersoon(doel.naam, c, amb)) || null;
    const titelNaam = crewNaam ? null
      : (crewUitTitel(r.origineleTitel || r.titel).find((x) => zelfdePersoon(doel.naam, x.naam, amb))?.naam || null);
    if (!crewNaam && !titelNaam) return;
    const gemarkeerdeDagen = doel.soort === 'druk' ? dagen.filter((d) => drukkeDagen.has(d)) : [];
    rijen.set(r.id, {
      optie: (doel.soort === 'over' || doel.soort === 'op_max') && isOptie(r),
      dagen: gemarkeerdeDagen,
      crewNaam,
      titelNaam,
    });
  });
  return { rijen, soort: doel.soort };
}
